import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Field, Form, Formik } from "formik";
import { Form as AntForm } from "antd";
import { OTPValidate, ResendOtpController } from "@njoy/controller";
import { validateOTPSchema } from "@njoy/common";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { TemplateSub } from "../../../components/layouts/TemplateSub";
import NewButton from "../../../components/uiMerge/newButton";
import { InputOTPField } from "../../shared/InputOTPField";
import ToastMessage from "../../../hooks/useToast";
import { extractError } from "../../../utils/extractError";

import * as ROUTES from "../../../constants/routes";

const FormItem = AntForm.Item;

export interface OTPValidateFormValues {
  verificationCode: string;
}

export const VerifyAccountPage: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const email = params.get("email");
  const [seconds, setSeconds] = useState(240);

  const padding = (num: number) => num.toString().padStart(2, "0");
  const timer = (type: "minutes" | "seconds") => {
    if (type === "minutes") return Math.floor(seconds / 60);
    return seconds % 60;
  };

  useEffect(() => {
    const activeTime = setInterval(() => setSeconds(seconds - 1), 1000);

    if (seconds <= 0) return clearInterval(activeTime);

    return () => {
      clearInterval(activeTime);
    };
  }, [seconds]);

  const onFinish = () => {
    window.location.replace(ROUTES.LOGIN);
  };

  return (
    <>
      <ToastContainer />

      <OTPValidate>
        {({ otpValidate }) => (
          <Formik<OTPValidateFormValues>
            validationSchema={validateOTPSchema}
            validateOnChange={true}
            validateOnBlur={true}
            initialValues={{
              verificationCode: ""
            }}
            onSubmit={async (
              { verificationCode }, 
              { resetForm, setSubmitting, setErrors }
            ) => {
              const response = await otpValidate({ variables: { verificationCode, email: email || "" } });
              setSubmitting(false);
              resetForm();

              if (!response || !response.data) {
                setErrors({ verificationCode: "No response from server. Please try again." });
                ToastMessage("error", "No response from server. Please try again.");
                return;
              }

              const result = response.data.otpValidate;
              if (result?.errors && result.errors.length > 0) {
                setErrors({ verificationCode: result.errors[0].message });
                ToastMessage("error", result.errors[0].message);
                return;
              }

              if (result?.message) {
                ToastMessage("success", result.message);
                onFinish();
              }
            }}>
            {({ isSubmitting }) => (
              <TemplateSub page={"Enter OTP"} showNavBar={false}>
                <div className="mt-8 mb-2 w-full flex-1 flex flex-col">
                  <p className="px-[3rem] text-[16px] py-10 text-center text-white">
                    We sent an OTP to <span className="font-bold">{email}</span>. 
                    Enter it below to continue.
                  </p>
                
                  <Form className="flex flex-col flex-1">
                    <Field
                      name="verificationCode"
                      component={InputOTPField}
                      otpLength={4}
                    />

                    <div className="mt-auto px-[1rem]">
                      <FormItem>
                        <NewButton
                          loading={isSubmitting}
                          fullWidth
                          variant="primary"
                          type="submit"
                        >
                          Create Account
                        </NewButton>
                      </FormItem>

                      <FormItem>
                        <div className="flex flex-row w-full justify-center text-white mr-2">
                          <span className="mr-1">Didn't receive an OTP?</span>
                          {seconds > 0 ? (
                            <>
                              <span className="cursor-not-allowed text-[color:var(--brand-primary)]">Resend&nbsp;</span>
                              {` in ${padding(timer("minutes"))}:${padding(timer("seconds"))} min`}
                            </>
                          ) : (
                            <ResendOtpController>
                              {({ resendOtp, loading, countdown, canResend }) => (
                                <>
                                  <button
                                    onClick={async () => {
                                      const response = await resendOtp({ email: email as string });
                                      if (response?.errors?.length) {
                                        const errorObj = response.errors.reduce(
                                          (
                                            acc: Record<string, string>,
                                            curr: { message: string },
                                            idx: number
                                          ) => {
                                            acc[idx] = curr.message;
                                            return acc;
                                          },
                                          {} as Record<string, string>
                                        );
                                        ToastMessage("error", extractError(errorObj));
                                      } else if (response?.message) {
                                        ToastMessage("success", response.message);
                                      }
                                    }}
                                    disabled={!canResend || loading}
                                    className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                                  >
                                    {
                                      canResend ? 
                                      <span className="cursor-pointer text-[color:var(--brand-primary)]">Resend</span> : 
                                      `Wait ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, "0")}`
                                    }
                                  </button>
                                </>
                              )}
                            </ResendOtpController>
                          )}
                        </div>
                      </FormItem>
                    </div>
                  </Form>
                </div>
              </TemplateSub>
            )}
          </Formik>
        )}
      </OTPValidate>
    </>
  );
};